import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  WSActions,
  WSButton,
  WSCard,
  WSControl,
  WSDivider,
  WSEmptyState,
  WSFlexBox,
  WSInfoBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { WSQueries } from "../../../../query/WSQuery";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { OnboardingStep } from "../../../Onboarding/types";
import { useFlowContext } from "../../../../shared/components/Flow";
import { useQueryMeridioPlanCatalog } from "../../../../query/benefits/useQueryMeridioPlanCatalog";
import { useMutationEnrollInMeridio } from "../../../../query/benefits/useMutationEnrollInMeridio";
import {
  IMeridioPlanCatalogCategory,
  IMeridioPlanCatalogResponse,
  IMeridioPlanOption
} from "../../../../services/api/benefits/meridioPlanCatalog/types";
import { routeHelpersMyResourcesTabs } from "../../../../modules/MyResources/paths";
import {
  PATH_BENEFITS_MERIDIO_ENROLL_SELECT_PLANS,
  PATH_BENEFITS_MERIDIO_ENROLL_SETUP_PAYMENT
} from "../../paths";
import { openMeridioGuidebook } from "../../utils";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { WS_LINKS } from "../../../../types/wsLinks";
import { CoverageTier, EnrollMeridioFlowState } from "./types";
import { useModalWhatIsQLE } from "./ModalWhatIsQLE";
import { MeridioNeedToMakeChanges } from "../../components/customBenefits/Meridio/MeridioNeedToMakeChanges";

const TIER_LABELS: Record<CoverageTier, string> = {
  IO: "Individual (just you)",
  IS: "Individual + Spouse",
  IC: "Individual + Dependents",
  IF: "Family"
};

const TIER_KEY_MAP: Record<CoverageTier, "io" | "is" | "ic" | "if"> = {
  IO: "io",
  IS: "is",
  IC: "ic",
  IF: "if"
};

function findPlan(
  categories: IMeridioPlanCatalogCategory[],
  baseSku: string
): IMeridioPlanOption | undefined {
  for (const cat of categories) {
    const found = (cat.plans as IMeridioPlanOption[]).find(
      p => p.baseSku === baseSku
    );
    if (found) return found;
  }
  return undefined;
}

const buildAckOptions = (firstPaymentDate: Date) => [
  {
    value: "1",
    label: (
      <WSText.ParagraphSm color="gray600">
        I understand coverage will not begin until my first payment is
        successfully processed by{" "}
        {toWSDateString(firstPaymentDate, "monthDayYear")}. If payment fails, my
        enrollment will be automatically cancelled and you must return to this
        step to enter a valid payment method to enroll again.
      </WSText.ParagraphSm>
    )
  },
  {
    value: "2",
    label: (
      <WSText.ParagraphSm color="gray600">
        I have reviewed the plan benefits, exclusions and limitations. I
        understand what is and is not covered.{" "}
        <WSButton.Link onClick={openMeridioGuidebook}>
          View plan benefits.
        </WSButton.Link>
      </WSText.ParagraphSm>
    )
  }
];

export const StepReviewConfirm: React.FC<OnboardingStep> = props => {
  const history = useHistory();
  const goToMyResourcesTab = routeHelpersMyResourcesTabs.useGoTo();
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();
  const [understoodPlanYear, setUnderstoodPlanYear] = useState(false);
  const [reviewedPlan, setReviewedPlan] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const consented =
    understoodPlanYear && reviewedPlan && acceptedPrivacy && acceptedTerms;
  const modalWhatIsQLE = useModalWhatIsQLE();
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentResponse, setEnrollmentResponse] = useState<any>(null);

  const queryMeridioPlanCatalog = useQueryMeridioPlanCatalog();
  const [enrollInMeridio, enrollInMeridioMeta] = useMutationEnrollInMeridio();

  const allSelectedSkus = useMemo(
    () =>
      [
        flowState.healthPlanSku,
        ...(flowState.voluntaryPlanSkus ?? []),
        flowState.dentalVisionPlanSku
      ].filter((s): s is string => !!s),
    [
      flowState.healthPlanSku,
      flowState.voluntaryPlanSkus,
      flowState.dentalVisionPlanSku
    ]
  );

  const coverageStartDate = useMemo(() => {
    const now = new Date();
    // If enrolled on or before the 10th → coverage starts 1st of next month
    // If enrolled after the 10th → coverage starts 1st of the month after next
    const monthsToAdd = now.getDate() <= 10 ? 1 : 2;
    return new Date(now.getFullYear(), now.getMonth() + monthsToAdd, 1);
  }, []);

  // Payment collection window is 12th–18th of the month before coverage starts
  const firstPaymentDate = useMemo(() => {
    return new Date(
      coverageStartDate.getFullYear(),
      coverageStartDate.getMonth() - 1,
      19
    );
  }, [coverageStartDate]);

  const ackOptions = useMemo(
    () => buildAckOptions(firstPaymentDate),
    [firstPaymentDate]
  );

  if (isEnrolled) {
    return (
      <WSList gap="2XL">
        <WSCard>
          <WSEmptyState
            type="done"
            title="Your enrollment was successful!"
            description="Congrats! You're now enrolled in benefits provided by Meridio through Wingspan."
          />
        </WSCard>
        <WSButton
          onClick={() => goToMyResourcesTab({ tab: "personal-insurance" })}
          fullWidth
        >
          View enrollment
        </WSButton>
        <WSCard>
          <WSText.Paragraph color="gray600" weight="medium" mb="L">
            What happens next?
          </WSText.Paragraph>
          <WSList gap="M">
            <WSInfoBox
              theme="neutral"
              title="What to expect next"
              icon="info-circle"
            >
              <WSList as="ol" gap="L">
                <li>
                  <b>First Payment (12th-19th of the month):</b>
                  <ul>
                    <li>
                      We'll charge your payment method on the 12th of the month.
                    </li>
                    <li>
                      If the payment doesn't go through, we'll automatically
                      retry daily until the 19th.
                    </li>
                    <li>✓ Payment successful → Your enrollment is confirmed</li>
                    <li>
                      ✗ Payment fails by{" "}
                      {toWSDateString(firstPaymentDate, "monthDayYear")} →
                      Enrollment automatically cancelled
                    </li>
                  </ul>
                </li>
                <li>
                  <b>Meridio Confirmation (20th of the month):</b>
                  <ul>
                    <li>
                      You'll receive an email from Meridio confirming your
                      coverage details.
                    </li>
                  </ul>
                </li>
                <li>
                  <b>
                    Insurance Cards Arrive (7-10 days after effective date):
                  </b>
                  <ul>
                    <li>Meridio will mail your ID cards to you.</li>
                  </ul>
                </li>
              </WSList>
            </WSInfoBox>
            <MeridioNeedToMakeChanges />
          </WSList>
        </WSCard>
      </WSList>
    );
  }

  return (
    <WSQueries queries={{ queryMeridioPlanCatalog }}>
      {({
        queryMeridioPlanCatalogData
      }: {
        queryMeridioPlanCatalogData: IMeridioPlanCatalogResponse;
      }) => {
        const totalMonthlyPremium = allSelectedSkus.reduce((sum, baseSku) => {
          const tier = flowState.planSelections?.[baseSku];
          if (!tier) return sum;
          const tierKey = TIER_KEY_MAP[tier];
          const plan = findPlan(
            queryMeridioPlanCatalogData.categories,
            baseSku
          );
          return sum + (plan?.[tierKey]?.monthlyPremium ?? 0);
        }, 0);

        return (
          <>
            <WSText.Heading3 weight="medium" mb="S">
              Confirm & enroll
            </WSText.Heading3>
            <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
              Review your information and complete enrollment.
            </WSText.ParagraphSm>

            <WSList gap="XL">
              {/* Your package */}
              <WSCard>
                <WSFlexBox.CenterY justify="space-between" mb="XS">
                  <WSText.ParagraphSm color="gray500" weight="medium">
                    Your package
                  </WSText.ParagraphSm>
                  <WSButton.Link
                    onClick={() =>
                      history.push(PATH_BENEFITS_MERIDIO_ENROLL_SELECT_PLANS)
                    }
                  >
                    Edit
                  </WSButton.Link>
                </WSFlexBox.CenterY>
                <WSText.ParagraphXs weight="book" color="gray400" mb="2XL">
                  Summary of your plans and premiums
                </WSText.ParagraphXs>

                <WSList gap="L">
                  {allSelectedSkus.map(baseSku => {
                    const plan = findPlan(
                      queryMeridioPlanCatalogData.categories,
                      baseSku
                    );
                    if (!plan) return null;
                    const tier = flowState.planSelections?.[baseSku];
                    const tierKey = tier ? TIER_KEY_MAP[tier] : undefined;
                    const pricing = tierKey ? plan[tierKey] : undefined;
                    return (
                      <WSFlexBox.CenterY key={baseSku} justify="space-between">
                        <WSText.ParagraphSm weight="book" color="gray500">
                          {plan.name}
                          {tier ? ` (${TIER_LABELS[tier]})` : ""}
                        </WSText.ParagraphSm>
                        <WSText.ParagraphSm weight="book" color="gray700">
                          {pricing
                            ? `${formatMoney(pricing.monthlyPremium, Currency.USD)}/mo`
                            : "—"}
                        </WSText.ParagraphSm>
                      </WSFlexBox.CenterY>
                    );
                  })}
                </WSList>

                <WSDivider my="L" type="split" />

                <WSFlexBox.CenterY justify="space-between" mb="2XL">
                  <WSText.ParagraphSm color="gray500" weight="medium">
                    Total monthly premium:
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm color="gray700" weight="medium">
                    {formatMoney(totalMonthlyPremium, Currency.USD)}/mo
                  </WSText.ParagraphSm>
                </WSFlexBox.CenterY>

                <WSInfoBox>
                  <WSText.ParagraphXs weight="medium" color="gray600" mt="S">
                    Coverage start date{" "}
                  </WSText.ParagraphXs>
                  <WSText.ParagraphXs weight="book" color="gray600">
                    {toWSDateString(coverageStartDate, "monthDayYear")}
                  </WSText.ParagraphXs>
                </WSInfoBox>
              </WSCard>

              {/* Acknowledgments */}
              <WSCard>
                <WSText.Paragraph color="gray600" weight="medium" mb="M">
                  Acknowledgments
                </WSText.Paragraph>

                <WSList gap="M">
                  <WSInfoBox icon="help-circle">
                    {"{Standard coverage and validation language by Meridio}"}
                    <WSText.ParagraphSm mt="XS">
                      <b>Insurer:</b> {"{Meridio info}"}
                    </WSText.ParagraphSm>
                  </WSInfoBox>

                  <WSInfoBox icon="accept">
                    <WSText.ParagraphSm weight="medium" color="gray700" mb="L">
                      By completing this enrollment, your plan selection,
                      dependent information, and SSN will be shared with Meridio
                      for enrollment processing. Payment information is not
                      shared.
                    </WSText.ParagraphSm>
                    <WSList gap="M">
                      <WSControl
                        type="checkbox"
                        size="S"
                        onChange={setUnderstoodPlanYear}
                        value={understoodPlanYear}
                        label={
                          <>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              I understand my plan selections are for the full
                              plan year. I can only change coverage if I have a
                              Qualifying Life Event (QLE).{" "}
                            </WSText.ParagraphSm>
                            <WSButton.Link
                              onClick={() => modalWhatIsQLE.open({})}
                            >
                              What is a QLE?
                            </WSButton.Link>
                          </>
                        }
                      />
                      <WSControl
                        type="checkbox"
                        size="S"
                        onChange={setReviewedPlan}
                        value={reviewedPlan}
                        label={
                          <>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              I have reviewed the plan benefits, exclusions, and
                              limitations. I understand what is and is not
                              covered.{" "}
                            </WSText.ParagraphSm>
                            <WSButton.Link onClick={openMeridioGuidebook}>
                              View plan benefits.
                            </WSButton.Link>
                          </>
                        }
                      />
                      <WSControl
                        type="checkbox"
                        size="S"
                        onChange={setAcceptedPrivacy}
                        value={acceptedPrivacy}
                        label={
                          <>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              I accept Meridio's{" "}
                            </WSText.ParagraphSm>
                            <WSButton.Link
                              onClick={() =>
                                openInNewTab(WS_LINKS.meridioPrivacyPolicy)
                              }
                            >
                              Privacy Policy
                            </WSButton.Link>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              .
                            </WSText.ParagraphSm>
                          </>
                        }
                      />
                      <WSControl
                        type="checkbox"
                        size="S"
                        onChange={setAcceptedTerms}
                        value={acceptedTerms}
                        label={
                          <>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              I accept Meridio's{" "}
                            </WSText.ParagraphSm>
                            <WSButton.Link
                              onClick={() =>
                                openInNewTab(WS_LINKS.meridioTermsOfUse)
                              }
                            >
                              Terms of Use
                            </WSButton.Link>
                            <WSText.ParagraphSm
                              weight="book"
                              color="gray700"
                              inline
                            >
                              .
                            </WSText.ParagraphSm>
                          </>
                        }
                      />
                    </WSList>
                  </WSInfoBox>

                  <WSText.ParagraphSm color="gray500" weight="book">
                    Wingspan Solutions LLC is not a licensed insurance agency
                    and is not the insurer. This coverage is underwritten by
                    Meridio.
                  </WSText.ParagraphSm>
                </WSList>
              </WSCard>
            </WSList>

            <WSErrorMessage
              error={enrollInMeridioMeta.error}
              contextKey="Other"
            />

            <WSActions
              mt="XL"
              alignment="fill"
              buttons={[
                {
                  kind: "Tertiary",
                  label: "Back",
                  onClick: () => {
                    setFlowState(prev => ({
                      ...prev,
                      performDeduction: undefined
                    }));
                    history.push(PATH_BENEFITS_MERIDIO_ENROLL_SETUP_PAYMENT);
                  }
                },
                {
                  label: "Confirm & enroll",
                  disabled: !consented,
                  loading: enrollInMeridioMeta.isLoading,
                  onAsyncClick: async () => {
                    await enrollInMeridio(
                      {
                        plans: allSelectedSkus.map(baseSku => ({
                          baseSku,
                          tier: flowState.planSelections![baseSku]
                        })),
                        dependents: (flowState.dependents ?? []).map(dep => ({
                          ...dep,
                          dob: new Date(dep.dob)
                        })),
                        performDeduction: flowState.performDeduction ?? true,
                        tobaccoUse: flowState.tobaccoUse ?? false
                      },
                      {
                        onSuccess: response => {
                          setEnrollmentResponse(response);
                          setIsEnrolled(true);
                        }
                      }
                    );
                  }
                }
              ]}
            />
          </>
        );
      }}
    </WSQueries>
  );
};
